import { motion } from "framer-motion";
import { Briefcase, Users, Globe2, FileCheck2, PhoneCall, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal, Chapter } from "@/components/Reveal";
import { CallButton, GetStartedButton } from "@/components/CTA";
import { SITE, IMAGES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const AUDIENCE = ["Businesses", "Executives", "Corporate Travelers", "Employees", "Consultants", "International Teams", "Frequent Travelers"];

const SUPPORT = [
  { icon: Briefcase, title: "Executive & Employee Passports", desc: "New applications, renewals, and urgent travel-document assistance for your people." },
  { icon: Globe2, title: "Business Visa Preparation", desc: "Application documentation aligned with each consulate's current requirements." },
  { icon: FileCheck2, title: "Document Authentication", desc: "Apostilles and legalization for corporate documents used internationally." },
  { icon: Users, title: "Team Coordination", desc: "Organized support for multiple travelers, project teams, and international assignments." },
  { icon: PhoneCall, title: "Responsive Communication", desc: "Direct access to our team when travel plans change or timelines tighten." },
];

export default function BusinessPage() {
  return (
    <>
      <Seo
        title="Corporate Passport & Visa Services for Businesses | Travel Passport and Visa LLC"
        siteName={SITE.siteName}
        description="Travel documentation support for businesses — passport, visa, and travel-document assistance for executives, employees, and international teams. Call 866-334-8472."
      />
      <section className="grain relative overflow-hidden bg-navy" data-testid="business-hero">
        <div className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
          <img src={IMAGES.executive} alt="Executive preparing for international business travel" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/75 to-navy/25" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-28">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block rounded-full border border-gold/40 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-gold"
          >
            Private Service Provider
          </motion.span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl" data-testid="business-h1">
            <span className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
                Travel Documentation
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.28, ease: EASE }}>
                Support for <span className="text-gold">Businesses</span>
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
            className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Keep your employees and business travelers prepared for international travel with professional passport, visa, and travel-document assistance.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <GetStartedButton label="DISCUSS YOUR BUSINESS NEEDS" testId="business-discuss-button" />
            <CallButton light testId="business-call-now" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="business-audience-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Chapter num="01" label="Who We Support" />
            <div className="flex max-w-3xl flex-wrap gap-2.5" data-testid="business-audience-chips">
              {AUDIENCE.map((a) => (
                <span key={a} className="rounded-full border border-slate-200 bg-slate-50 px-5 py-2 text-sm font-medium text-navy">
                  {a}
                </span>
              ))}
            </div>
          </Reveal>
          <div className="mt-16">
            <Reveal>
              <Chapter num="02" label="How We Help" />
              <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-navy" data-testid="business-support-heading">
                Organized Documentation Support for Your Travelers
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {SUPPORT.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.07}>
                  <div className="h-full rounded-2xl border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-lg" data-testid={`business-support-${i}`}>
                    <s.icon className="h-7 w-7 text-gold-dark" />
                    <h3 className="mt-4 font-display text-lg font-bold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.35}>
                <div className="flex h-full flex-col justify-between rounded-2xl bg-navy p-7" data-testid="business-cta-card">
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">Talk to Our Team</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-300">
                      Tell us about your travelers and destinations — we'll explain how we can help and what fees apply.
                    </p>
                  </div>
                  <div className="mt-6 flex flex-col gap-3">
                    <GetStartedButton label="GET STARTED" testId="business-card-get-started" />
                    <CallButton light label={`CALL NOW — ${SITE.phoneDisplay}`} testId="business-card-call" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <Chapter num="03" label="Get Started" />
            <h2 className="font-display text-3xl font-bold tracking-tight text-navy" data-testid="business-form-heading">
              Tell Us About Your Business Travel Needs
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Professional assistance, never government-affiliation claims",
                "Clear separation of government fees and our service fees",
                "Processing times explained honestly — estimates only, never guaranteed",
                "More than 20 years assisting U.S. travelers and businesses",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" /> {b}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div id="get-started" className="scroll-mt-28 rounded-2xl bg-white p-7 shadow-xl ring-1 ring-slate-100 sm:p-8" data-testid="business-form-card">
              <div className="gold-rule mb-5 w-14" />
              <LeadForm source="business-page" testId="business-lead-form" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
